import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { nanoid } from "nanoid";
import Auth, { useAuth } from "shared/@auth";

import { useDashboard } from "ManagementTeam/DashboardPage/useDashboard";
import { useDocuments } from "./useDocuments";

import { Header } from "../Shared/Header/Header";
import DocumentItem from "ManagementTeam/Shared/DocumentItem";
import AddItemButton from "ManagementTeam/Shared/AddItemButton";
import TabBar from "ManagementTeam/Shared/TabBar";
import AddDocumentModal from "./AddDocumentModal";

import DocumentsIcon from "./Documents_Icon.svg";

import styles from "./DocumentsPage.module.css";
import { AddDocumentModalMemo } from "./AddDocumentModal/AddDocumentModal";
import Loader from "shared/Loader";

export function DocumentsPage({ meetingType, isHideInfo, setToggleInfo }) {
  const { account } = useAuth();
  const { hasPermission } = Auth.useContainer();

  const id = nanoid();
  const readOnly = !hasPermission("management-team.admins");
  const [refetchInterval, setRefetchInterval] = useState(1000 * 10);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: dashboardData,
    refetchDashboard,
    isFetching: isDashboardFetching,
  } = useDashboard(refetchInterval, true, true);
  const {
    reviewDocuments,
    reviewRefetch,
    refDocuments,
    refRefetch,
    unassignedRefetch,
    addDocumentMutation,
    editDocumentMutation,
    docDelete,
    docPublish,
    documentOrderPut,
    reviewStatus,
    refStatus,
  } = useDocuments({ enableExtras: true });

  const [dashboard, setDashboard] = useState([]);
  const [document, setDocument] = useState(null);
  const [reviewDocumentItems, setReviewDocumentItems] = useState([]);
  const [refDocumentItems, setRefDocumentItems] = useState([]);
  const [userList, setUserList] = useState([]);
  const [meetingDates, setMeetingDates] = useState([]);
  const [agendas, setAgendas] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [tab, setTab] = useState("Documents for Review");
  const [isOpen, setIsOpen] = useState(false);
  const [updateDocument, setUpdateDocument] = useState(null); //not sure why we need this when we already have Document?

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const refetchPromises = [
        refetchDashboard(),
        unassignedRefetch && unassignedRefetch(),
      ];

      // Only refetch the data for the active tab
      if (tab === "Documents for Review") {
        refetchPromises.push(reviewRefetch());
      } else {
        refetchPromises.push(refRefetch());
      }

      await Promise.all(refetchPromises);
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    if (isOpen) {
      setRefetchInterval(false);
    } else {
      setRefetchInterval(1000 * 10);
    }
  }, [isOpen]);
  useEffect(() => {
    setDashboard(
      dashboardData && dashboardData.meetings ? dashboardData.meetings : []
    );
  }, [dashboardData]);

  useEffect(() => {
    // populate ref/review docs on successful api response
    reviewDocuments && setReviewDocumentItems(reviewDocuments);
  }, [reviewDocuments]);

  useEffect(() => {
    refDocuments && setRefDocumentItems(refDocuments);
  }, [refDocuments]);

  useEffect(() => {
    // populate list of agendas with agenda ID
    if (!dashboard || dashboard.length === 0) return;

    const agendaList = dashboard.map((meeting) => {
      return {
        meetingId: meeting._id,
        agenda: meeting.agenda,
      };
    });

    setAgendas(agendaList);
  }, [dashboard]);

  useEffect(() => {
    // populate list of users for document assignment

    if (!dashboard || dashboard.length == 0) return;

    let _userList = [];

    dashboard[0].users.forEach((user) => {
      if (
        !user ||
        !user.businessInfo ||
        !user.businessInfo.qualifyingRequirementsByYear
      )
        return;

      const firstName = user.profile.firstName;
      const lastName = user.profile.lastName;
      const _user = {
        id: user._id,
        option: `${capitalizeFirstLetter(firstName)} ${capitalizeFirstLetter(
          lastName
        )}`,
        firstName: firstName,
        lastName: lastName,
      };

      const allowedFirstName = ["Attorneys", "WWG", "TSIKA", "WWG Guest"];
      const isAllowedByFirstName = allowedFirstName.find(
        (a) => a.toLowerCase() === firstName.toLowerCase()
      );

      if (isAllowedByFirstName) {
        _userList.unshift(_user);
        return;
      }

      const requirement = user.businessInfo.qualifyingRequirementsByYear.find(
        (year) => year.year === new Date().getFullYear()
      );
      if (requirement && requirement.isVotingMember) {
        _userList.push(_user);
        return;
      }

      return;
    });

    setUserList(
      _userList.sort((a, b) => {
        const result = a.lastName.localeCompare(b.lastName);

        return result !== 0 ? result : a.firstName.localeCompare(b.firstName);
      })
    );
  }, [dashboard]);

  useEffect(() => {
    // populate list of meeting dates with meeting ID
    if (!dashboard || dashboard.length === 0) return;

    const meetingList = dashboard
      .sort(
        (a, b) => new Date(a.dates[0].startsOn) - new Date(b.dates[0].startsOn)
      )
      .map((i) => {
        const dates =
          i.dates && i.dates.length > 0
            ? i.dates.map(
                (date) => new Date(date.startsOn.toLocaleString("en-US"))
              )
            : [];

        const month =
          dates.length > 0
            ? dates[0].toLocaleString("default", { month: "long" })
            : "";

        const days =
          dates.length > 0
            ? dates.map((date) => ` ${String(date.getDate())}`)
            : "";

        const year = dates.length > 0 ? String(dates[0].getFullYear()) : "";

        return { id: i._id, option: `${month}${days} ${year}` };
      });

    setMeetingDates(meetingList);
  }, [dashboard]);

  const onDragEndReview = useCallback(
    async (result) => {
      const { destination, source } = result;
      if (!destination) {
        return;
      }
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      ) {
        return;
      }

      let newItems = [...reviewDocumentItems];
      const [reorderedItem] = newItems.splice(result.source.index, 1);
      newItems.splice(result.destination.index, 0, reorderedItem);

      setReviewDocumentItems(newItems);

      documentOrderPut
        .mutateAsync({
          documentId: reorderedItem.id,
          order: result.destination.index,
        })
        .then(() => {
          reviewRefetch().then((res) => {
            if (res && res.data) {
              setReviewDocumentItems(res.data);
            }
          });
        });
    },
    [documentOrderPut, reviewDocumentItems, reviewRefetch]
  );

  const onDragEndRef = useCallback(
    async (result) => {
      const { destination, source } = result;
      if (!destination) {
        return;
      }
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      ) {
        return;
      }

      let newItems = [...refDocumentItems];
      const [reorderedItem] = newItems.splice(result.source.index, 1);
      newItems.splice(result.destination.index, 0, reorderedItem);

      setRefDocumentItems(newItems);

      documentOrderPut
        .mutateAsync({
          documentId: reorderedItem.id,
          order: result.destination.index,
        })
        .then(() => {
          refRefetch().then((res) => {
            if (res && res.data) {
              setRefDocumentItems(res.data);
            }
          });
        });
    },
    [documentOrderPut, refDocumentItems, refRefetch]
  );

  const capitalizeFirstLetter = (string) => {
    var _string = string.toLowerCase();
    return _string.charAt(0).toUpperCase() + _string.slice(1);
  };

  const onSave = (info) => {
    setIsOpen(false);
    setIsEdit(false);

    if (!isEdit) {
      addDocumentMutation.mutate(info, {
        onSuccess: () => {
          // Refetch the correct tab data based on document type
          if (info.isReview) {
            reviewRefetch();
          } else {
            refRefetch();
          }
        },
      });
      return;
    }

    var putObject = {
      ...info,
      id: document._id,
      meetingType: info.meetingType,
      title: info.title,
      owner: info.owner,
      description: info.description,
      agendaId: info.agendaId,
      isReview: info.isReview,
      notes: info.notes,
      isDownloadable: info.isDownloadable,
    };
    editDocumentMutation.mutate(putObject, {
      onSuccess: () => {
        // Refetch the correct tab data based on document type
        if (putObject.isReview) {
          reviewRefetch();
        } else {
          refRefetch();
        }
      },
    });
  };

  const onPublish = async (documentPublish) => {
    setIsOpen(false);
    setIsEdit(false);

    await callPublish(documentPublish);
  };

  const callPublish = async (documentPublish) => {
    if (!documentPublish || documentPublish.isDraft == null) return;

    const payload = {
      id: documentPublish.id,
      isDraft: documentPublish.isDraft,
    };

    await docPublish.mutateAsync(payload, {
      onSuccess: () => {
        // Refetch the correct tab data based on document type
        if (documentPublish.isReview) {
          reviewRefetch();
        } else {
          refRefetch();
        }
      },
    });
  };

  const onEdit = (info) => {
    if (!info.files) {
      info.files = [];
    }

    setDocument(info);
    setUpdateDocument(info);
    setIsEdit(true);
    setIsOpen(true);
  };

  const documentMemo = useMemo(() => {
    return {
      id: nanoid(),
      isReview: tab === "Documents for Review" ? true : false,
      agendas,
      meetingDates,
      userList:
        userList.length > 0
          ? userList.map((user) => user)
          : ["Error Retrieving API Data"],
      currentUser: account && account.name,
      updateDocument,
      meetingType,
    };
  }, [
    updateDocument,
    agendas,
    meetingDates,
    userList,
    account,
    meetingType,
    tab,
  ]);

  return (
    <>
      {isOpen && (
        <div className={styles.modal}>
          <AddDocumentModalMemo
            key={documentMemo.id}
            isOpen={true}
            onClose={() => {
              setIsOpen(false);
              setIsEdit(false);
            }}
            onSave={(info) => onSave(info)}
            onSaveDraft={(info) => onSave(info)}
            onPublish={(documentPublish) => onPublish(documentPublish)}
            isReview={documentMemo.isReview}
            agendaItems={documentMemo.agendas}
            meetings={documentMemo.meetingDates}
            itemOwners={documentMemo.userList}
            currentUser={documentMemo.currentUser}
            document={documentMemo.updateDocument}
            meetingType={documentMemo.meetingType}
          />
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.header}>
          <Header
            title={"Documents"}
            icon={DocumentsIcon}
            onRefresh={handleRefresh}
            isLoading={isRefreshing}
            children={
              <div className={styles.tabBarContainer}>
                <TabBar
                  titleList={[
                    {
                      title: "Documents for Review",
                    },
                    {
                      title: "Diamond Reference",
                    },
                  ]}
                  activeTitle={tab}
                  onChange={setTab}
                />
              </div>
            }
            isLock={true}
            isHide={isHideInfo}
            setToggleInfo={setToggleInfo}
          />
        </div>
        <div className={styles.lowerContainer}>
          <div
            style={{ visibility: !readOnly ? "visible" : "hidden" }}
            className={styles.addItemButtonContainer}
          >
            <AddItemButton
              onClick={() => {
                setIsOpen(true);
                setDocument(null);
                setUpdateDocument(null);
              }}
            />
            <div className={styles.addItemButtonText}>ADD NEW DOCUMENT</div>
          </div>
        </div>

        <div className={styles.lowerContainer}>
          {tab === "Documents for Review" ? (
            <>
              {reviewStatus === "success" ? (
                <>
                  {reviewDocumentItems.length > 0 ? (
                    <DragDropContext onDragEnd={onDragEndReview}>
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {reviewDocumentItems
                              .filter((item) => {
                                if (isHideInfo) {
                                  // Filter out unpublished documents
                                  if (!readOnly && item.isDraft) {
                                    return false;
                                  }
                                }
                                return true;
                              })
                              .map((item, index) => (
                                <div
                                  key={index}
                                  className={styles.documentItem}
                                >
                                  <Draggable
                                    draggableId={index.toString()}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <DocumentItem
                                          readOnly={readOnly}
                                          title={item.title}
                                          description={
                                            item.description
                                              ? item.description
                                              : ""
                                          }
                                          isDraft={item.isDraft}
                                          files={item.files}
                                          type={
                                            item.meetingType === "MT"
                                              ? "WWG"
                                              : ""
                                          }
                                          onPublish={() =>
                                            callPublish({
                                              id: item.id,
                                              isDraft: false,
                                              isReview: item.isReview,
                                            })
                                          }
                                          onEdit={() => onEdit(item)}
                                          onDelete={() =>
                                            docDelete.mutate(item.id, {
                                              onSuccess: () => {
                                                reviewRefetch();
                                              },
                                            })
                                          }
                                          separate={true}
                                          reorganize={true}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                </div>
                              ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  ) : (
                    <div className={styles.loading}>
                      No Documents to display...
                    </div>
                  )}
                </>
              ) : reviewStatus === "loading" ? (
                <div className={styles.loading}>
                  <Loader />
                </div>
              ) : (
                <div className={styles.loading}>
                  Error: Please contact support or try again later.
                </div>
              )}
            </>
          ) : (
            <>
              {refStatus === "success" ? (
                <>
                  {refDocumentItems.length > 0 ? (
                    <DragDropContext onDragEnd={onDragEndRef}>
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {refDocumentItems
                              .filter((item) => {
                                if (isHideInfo) {
                                  // Filter out unpublished documents
                                  if (!readOnly && item.isDraft) {
                                    return false;
                                  }
                                }
                                if (item.isDeleted) {
                                  return false;
                                }
                                return true;
                              })
                              .map((item, index) => (
                                <div
                                  key={index}
                                  className={styles.documentItem}
                                >
                                  <Draggable
                                    draggableId={index.toString()}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <DocumentItem
                                          readOnly={readOnly}
                                          title={item.title}
                                          description={
                                            item.description
                                              ? item.description
                                              : ""
                                          }
                                          isDraft={item.isDraft}
                                          files={item.files}
                                          type={
                                            item.meetingType === "MT"
                                              ? "WWG"
                                              : ""
                                          }
                                          onPublish={() =>
                                            callPublish({
                                              id: item.id,
                                              isDraft: false,
                                              isReview: item.isReview,
                                            })
                                          }
                                          onEdit={() => onEdit(item)}
                                          onDelete={() =>
                                            docDelete.mutate(item.id, {
                                              onSuccess: () => {
                                                refRefetch();
                                              },
                                            })
                                          }
                                          separate={true}
                                          reorganize={true}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                </div>
                              ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  ) : (
                    <div className={styles.loading}>
                      No Documents to display...
                    </div>
                  )}
                </>
              ) : refStatus === "loading" ? (
                <div className={styles.loading}>
                  <Loader />
                </div>
              ) : (
                <div className={styles.loading}>
                  Error: Please contact support or try again later.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
